import { useAuth0 } from "@auth0/auth0-react";
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Container,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  Snackbar,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import useColors from "../api/hooks/useColors";
import AppPage from "../components/structure/AppPage";
import CodeBlock from "./CodeBlock";
import componentsData from "./componentsData";
import { groupByCategory } from "./utils";

/**
 * ComponentDocs - A documentation page for shared components
 * Displays component information, props, examples, and interactive demos
 */
function ComponentDocs() {
  const [copySnackbar, setCopySnackbar] = useState({
    open: false,
    message: "",
  });
  const [expandedComponents, setExpandedComponents] = useState({});
  const theme = useTheme();
  const groupedComponents = groupByCategory(componentsData);
  const categories = Object.keys(groupedComponents).sort();
  const { user } = useAuth0();
  const colors = useColors();
  // Initialize all components as expanded by default
  useEffect(() => {
    const initialExpandState = {};
    componentsData.forEach((component) => {
      initialExpandState[component.id] = true; // Start expanded
    });
    setExpandedComponents(initialExpandState);
  }, []);

  const handleCopyCode = (code) => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopySnackbar({ open: true, message: "Code copied to clipboard!" });
      })
      .catch((err) => {
        setCopySnackbar({ open: true, message: "Failed to copy code: " + err });
      });
  };

  const handleCloseSnackbar = () => {
    setCopySnackbar({ ...copySnackbar, open: false });
  };

  const toggleComponentExpand = (componentId) => {
    setExpandedComponents((prev) => ({
      ...prev,
      [componentId]: !prev[componentId],
    }));
  };

  // Expand all components
  const expandAll = () => {
    const allExpanded = {};
    componentsData.forEach((component) => {
      allExpanded[component.id] = true;
    });
    setExpandedComponents(allExpanded);
  };

  // Collapse all components
  const collapseAll = () => {
    const allCollapsed = {};
    componentsData.forEach((component) => {
      allCollapsed[component.id] = false;
    });
    setExpandedComponents(allCollapsed);
  };

  // Render the component documentation
  const renderComponentSection = (component) => {
    const isExpanded = expandedComponents[component.id];

    return (
      <Box
        key={component.id}
        id={component.id}
        sx={{
          mb: 3,
          scrollMarginTop: "70px",
        }}
      >
        <Box
          sx={{
            overflow: "hidden",
            borderRadius: 3,
            border: `1px solid ${colors.divider}`,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              boxShadow: theme.shadows[3],
            },
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: isExpanded ? "1px solid" : "none",
              borderColor: alpha(theme.palette.primary.main, 0.1),
              background: `linear-gradient(to right, ${alpha(
                colors.primary_main,
                0.05
              )}, transparent)`,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
            onClick={() => toggleComponentExpand(component.id)}
          >
            <Box>
              <Box display="flex" alignItems="center" mb={0.5}>
                <Typography
                  variant="h6"
                  fontWeight="500"
                  sx={{ fontSize: "1.1rem" }}
                >
                  {component.name}
                </Typography>
                <Chip
                  label={component.category}
                  size="small"
                  sx={{
                    ml: 1,
                    fontSize: "0.65rem",
                    height: "20px",
                    borderRadius: 4,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.dark,
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {component.description}
              </Typography>
            </Box>

            <Box>
              {isExpanded ? (
                <ExpandLessIcon
                  fontSize="small"
                  sx={{
                    color: theme.palette.text.secondary,
                    transition: "all 0.2s",
                  }}
                />
              ) : (
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    color: theme.palette.text.secondary,
                    transition: "all 0.2s",
                  }}
                />
              )}
            </Box>
          </Box>

          <Collapse in={isExpanded}>
            <Grid container>
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  p: 2,
                  borderRight: {
                    md: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                  },
                }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    color: theme.palette.primary.main,
                  }}
                >
                  Props
                </Typography>
                <Box sx={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "0.85rem",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.03
                          ),
                        }}
                      >
                        <th
                          style={{
                            textAlign: "left",
                            padding: "8px",
                            borderBottom: `1px solid ${alpha(
                              theme.palette.primary.main,
                              0.1
                            )}`,
                            borderTopLeftRadius: "8px",
                          }}
                        >
                          Name
                        </th>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "8px",
                            borderBottom: `1px solid ${alpha(
                              theme.palette.primary.main,
                              0.1
                            )}`,
                          }}
                        >
                          Type
                        </th>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "8px",
                            borderBottom: `1px solid ${alpha(
                              theme.palette.primary.main,
                              0.1
                            )}`,
                          }}
                        >
                          Description
                        </th>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "8px",
                            borderBottom: `1px solid ${alpha(
                              theme.palette.primary.main,
                              0.1
                            )}`,
                            borderTopRightRadius: "8px",
                          }}
                        >
                          Default
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {component.props.map((prop, index) => (
                        <tr
                          key={index}
                          style={{
                            backgroundColor:
                              index % 2 === 0
                                ? "transparent"
                                : alpha(theme.palette.primary.main, 0.01),
                          }}
                        >
                          <td
                            style={{
                              padding: "8px",
                              borderBottom: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.05
                              )}`,
                            }}
                          >
                            <code
                              style={{
                                fontSize: "0.8rem",
                                backgroundColor: alpha(
                                  theme.palette.secondary.main,
                                  0.05
                                ),
                                padding: "2px 4px",
                                borderRadius: "4px",
                              }}
                            >
                              {prop.name}
                            </code>
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              borderBottom: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.05
                              )}`,
                            }}
                          >
                            <code
                              style={{
                                fontSize: "0.8rem",
                                backgroundColor: alpha(
                                  theme.palette.info.main,
                                  0.05
                                ),
                                padding: "2px 4px",
                                borderRadius: "4px",
                              }}
                            >
                              {prop.type}
                            </code>
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              borderBottom: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.05
                              )}`,
                            }}
                          >
                            {prop.description}
                          </td>
                          <td
                            style={{
                              padding: "8px",
                              borderBottom: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.05
                              )}`,
                            }}
                          >
                            {prop.defaultValue ? (
                              <code
                                style={{
                                  fontSize: "0.8rem",
                                  backgroundColor: alpha(
                                    theme.palette.success.main,
                                    0.05
                                  ),
                                  padding: "2px 4px",
                                  borderRadius: "4px",
                                }}
                              >
                                {prop.defaultValue}
                              </code>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Grid>

              <Grid item xs={12} md={6} sx={{ p: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={0.5}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: "500",
                      color: theme.palette.primary.main,
                    }}
                  >
                    Usage Example
                  </Typography>
                  <Button
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent toggling when clicking the button
                      handleCopyCode(component.example);
                    }}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: "24px",
                      fontSize: "0.7rem",
                      borderRadius: 2,
                      textTransform: "none",
                    }}
                  >
                    Copy Code
                  </Button>
                </Box>
                <Box sx={{ borderRadius: 2, overflow: "hidden", mb: 2 }}>
                  <CodeBlock code={component.example} />
                </Box>

                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    color: theme.palette.primary.main,
                  }}
                >
                  Live Demo
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    border: `1px dashed ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}`,
                    borderRadius: 2,
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.5
                    ),
                  }}
                >
                  {component.demo && <component.demo />}
                </Box>
              </Grid>
            </Grid>
          </Collapse>
        </Box>
      </Box>
    );
  };

  return user.email.includes("@breeze") ? (
    <AppPage title="Component Documentation">
      <Container maxWidth="xl">
        <Box sx={{ display: "flex", gap: 3, mt: 2, pb: 4 }}>
          {/* Sidebar navigation */}
          <Box
            sx={{
              width: 200,
              flexShrink: 0,
              position: "sticky",
              top: 24,
              alignSelf: "flex-start",
              maxHeight: "calc(100vh - 48px)",
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 4,
              },
            }}
          >
            <Box
              sx={{
                borderRadius: "10px",
                overflow: "hidden",
                border: `1px solid ${colors.divider}`,
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: `1px solid ${colors.divider}`,
                  backgroundColor: colors.grey_200,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="500"
                  fontSize="0.95rem"
                  color={colors.common_black}
                >
                  Components
                </Typography>
              </Box>
              <Box>
                {categories.map((category) => (
                  <Box key={category}>
                    <Typography
                      variant="overline"
                      sx={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        textTransform: "capitalize",
                        color: colors.primary_dark,
                        px: 1.5,
                        py: 0.75,
                        backgroundColor: colors.primary_8percent,
                      }}
                    >
                      {category}
                    </Typography>
                    <List dense disablePadding>
                      {groupedComponents[category].map((component) => (
                        <ListItem
                          key={component.id}
                          disablePadding
                          sx={{ display: "block" }}
                        >
                          <Link
                            href={`#${component.id}`}
                            underline="none"
                            onClick={() => {
                              // Ensure the component is expanded when clicked in the sidebar
                              if (!expandedComponents[component.id]) {
                                toggleComponentExpand(component.id);
                              }
                            }}
                            sx={{
                              display: "block",
                              px: 2,
                              py: 0.75,
                              fontSize: "0.85rem",
                              color: "text.primary",
                              "&:hover": {
                                bgcolor: colors.primary_4percent,
                                color: colors.primary_main,
                                borderLeft: `3px solid ${colors.primary_main}`,
                              },
                            }}
                          >
                            <Typography variant="caption">
                              {component.name}
                            </Typography>
                          </Link>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Main content */}
          <Box sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: colors.common_white,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography
                  variant="body2"
                  paragraph
                  sx={{ maxWidth: 700, mb: 1 }}
                >
                  This documentation provides usage examples, props reference,
                  and live demos for our shared components.
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
                position: "sticky",
                top: 0,
                pt: 2,
                pb: 2,
                backgroundColor: colors.common_white,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={expandAll}
                sx={{ mr: 1, fontSize: "0.75rem" }}
              >
                Expand All
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={collapseAll}
                sx={{ fontSize: "0.75rem" }}
              >
                Collapse All
              </Button>
            </Box>
            {categories.map((category) => (
              <Box key={category} sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1.5,
                    pb: 0.5,
                    borderBottom: `1px solid ${colors.divider}`,
                    fontWeight: 500,
                    color: colors.primary_dark,
                  }}
                >
                  {category}
                </Typography>
                {groupedComponents[category].map((component) =>
                  renderComponentSection(component)
                )}
              </Box>
            ))}
          </Box>
        </Box>

        <Snackbar
          open={copySnackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          message={copySnackbar.message}
          sx={{
            "& .MuiSnackbarContent-root": {
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.primary.main, 0.9),
            },
          }}
          action={
            <IconButton
              size="small"
              color="inherit"
              onClick={handleCloseSnackbar}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </Container>
    </AppPage>
  ) : (
    <AppPage title="Component Documentation">
      <Typography>You are not authorized to access this page.</Typography>
    </AppPage>
  );
}

export default ComponentDocs;
